import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading, Text } from "@/components/ui/typography"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { sfIcons } from '@/lib/sf-icons'

export type PaymentModelsProps = { id?: string }

export function PaymentModels({ id }: PaymentModelsProps) {
  const { t } = useTranslation("marketing")
  
  const scenarios = [
    {
      id: "freelancer",
      icon: sfIcons.user,
      role: t("scenarios.freelancer.role"),
      problem: t("scenarios.freelancer.problem"),
      solution: t("scenarios.freelancer.solution"),
    },
    {
      id: "business",
      icon: sfIcons.users,
      role: t("scenarios.business.role"),
      problem: t("scenarios.business.problem"),
      solution: t("scenarios.business.solution"),
    },
    {
      id: "platform",
      icon: sfIcons.layoutGrid,
      role: t("scenarios.platform.role"),
      problem: t("scenarios.platform.problem"),
      solution: t("scenarios.platform.solution"),
    },
  ]
  return (
    <section
      id={id}
      aria-labelledby="payment-models-title"
      className={cn(
        "py-16 sm:py-24",
        semanticTokens.bg.surface
      )}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="space-y-3 sm:space-y-4 text-center md:text-left">
          <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
            {t("paymentModels.badge")}
          </Text>
          <Heading level={2} id="payment-models-title">
            {t("paymentModels.headline")}
          </Heading>
          <Text variant="lg" color="muted" className="max-w-2xl">
            {t("paymentModels.description")}
          </Text>
        </div>

        <div className="mt-8 sm:mt-10 md:mt-12 grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 md:grid-cols-3">
          {scenarios.map((scenario) => {
            const Icon = scenario.icon || sfIcons.user
            if (!Icon) return null
            return (
              <SpotlightCard
                key={scenario.id}
                className={cn(
                  // CANON: 240ms standard motion
                  semanticTokens.motion.standard,
                  "flex flex-col transition-all hover:shadow-xl hover:-translate-y-1",
                  "p-6 sm:p-7 md:p-8"
                )}
              >
                <div className={cn(
                  "inline-flex items-center justify-center rounded-xl mb-6 sm:mb-7 md:mb-8 transition-colors",
                  "h-12 w-12 sm:h-13 sm:w-13 md:h-14 md:w-14",
                  "border-2", semanticTokens.border.default, semanticTokens.fg.muted,
                  "group-hover:border-border/80 group-hover:text-foreground"
                )}>
                  <Icon className="h-6 w-6 sm:h-6.5 sm:w-6.5 md:h-7 md:w-7" />
                </div>

                <Heading level={3} className="mb-3">
                  {scenario.role}
                </Heading>

                <div className="space-y-4 sm:space-y-5 md:space-y-6 flex-1 flex flex-col">
                  <div>
                    <Text variant="caption-sm" className={cn("font-bold uppercase tracking-wider mb-2", semanticTokens.fg.subtle)}>
                      {t("paymentModels.painLabel")}
                    </Text>
                    <Text variant="sm" color="muted" className="leading-relaxed italic">
                      "{scenario.problem}"
                    </Text>
                  </div>

                  <div className={cn("mt-auto pt-5 sm:pt-6 border-t", semanticTokens.border.subtle)}>
                    <Text variant="caption-sm" className={cn("font-bold uppercase tracking-wider mb-2", semanticTokens.info.accent)}>
                      {t("paymentModels.solutionLabel")}
                    </Text>
                    <Text variant="sm" color="default" className="font-medium leading-relaxed">
                      {scenario.solution}
                    </Text>
                  </div>
                </div>
              </SpotlightCard>
            )
          })}
        </div>
      </div>
    </section>
  )
}
